import { useToast } from '@/contexts/ToastContext';
import { useSupaSocket } from '@/hooks/useSupaSocket';
import {
    WorkspaceMember as APIWorkspaceMember,
    featureService,
    OwnerTransferPending,
    PendingWorkspaceInvite,
    WorkspaceMembersEntitlements,
    WorkspaceInvitation,
    InviteProjectAccessGrant,
} from '@common/api/featureService';
import { getCurrentProductCode, getProductScopedStorageKey } from '@common/api/productContext';
import { useUserStorage } from '@common/hooks/useUserStorage';
import {
    useCallback, useEffect, useRef, useState
} from 'react';
import {
    getPendingOwnerTransferRefreshDelay,
    normalizePendingOwnerTransfer,
} from './ownerTransferState';
import { MemberStatus, WorkspaceRole } from './useWorkspaces';

export type {
    MemberStatus,
    OwnerTransferPending,
    PendingWorkspaceInvite,
    WorkspaceInvitation,
    APIWorkspaceMember as WorkspaceMember,
    WorkspaceRole
};

// 计算属性：用于 UI 显示的成员信息
export interface WorkspaceMemberDisplay extends APIWorkspaceMember {
    displayName: string;
    avatar: string;
    isCurrentUser: boolean;
}

// Hook 返回类型
export interface UseWorkspaceMembersReturn {
    members: WorkspaceMemberDisplay[];
    invitations: WorkspaceInvitation[];
    pendingInvites: PendingWorkspaceInvite[];
    entitlements: WorkspaceMembersEntitlements;
    pendingOwnerTransfer: OwnerTransferPending | null;
    status: WorkspaceMembersStatus;
    isLoading: boolean;
    isLoadingInvitations: boolean;
    error: Error | null;
    refresh: () => Promise<void>;
    refreshInvitations: () => Promise<void>;
    refreshPendingOwnerTransfer: () => Promise<void>;
    // CRUD 操作
    inviteMember: (
        email: string,
        role: WorkspaceRole,
        dataRoleId?: number | string | null,
        projectAccessGrants?: InviteProjectAccessGrant[],
    ) => Promise<void>;
    removeMember: (memberId: string) => Promise<void>;
    updateMemberRole: (memberId: string, role: WorkspaceRole) => Promise<void>;
    transferOwnership: (targetUserId: string, confirmationText: string) => Promise<void>;
    cancelOwnerTransfer: (requestId: string) => Promise<void>;
    cancelInvite: (token: string) => Promise<void>;
    // Membership/ownership 和展示角色必须保持独立语义。
    currentMembershipRole: WorkspaceRole | null;
    currentWorkspaceRole: string | null;
}

type UseWorkspaceMembersOptions = {
    enabled?: boolean;
};

const normalizeMembershipRoleValue = (role?: string | null): WorkspaceRole | null => {
    const normalized = String(role || '').trim().toUpperCase();
    if (!normalized) return null;
    return normalized === 'OWNER' ? 'OWNER' : 'MEMBER';
};

export type WorkspaceMembersStatus = 'idle' | 'loading' | 'refreshing' | 'ready' | 'error';

type WorkspaceMembersState = {
    workspaceId: string | null;
    status: WorkspaceMembersStatus;
    members: WorkspaceMemberDisplay[];
    pendingInvites: PendingWorkspaceInvite[];
    entitlements: WorkspaceMembersEntitlements;
    pendingOwnerTransfer: OwnerTransferPending | null;
    error: Error | null;
    currentMembershipRole: WorkspaceRole | null;
    currentWorkspaceRole: string | null;
};

const createWorkspaceMembersState = ({
    workspaceId,
    status,
    members = [],
    pendingInvites = [],
    entitlements = {},
    pendingOwnerTransfer = null,
    error = null,
    currentMembershipRole = null,
    currentWorkspaceRole = null,
}: {
    workspaceId: string | null;
    status: WorkspaceMembersStatus;
    members?: WorkspaceMemberDisplay[];
    pendingInvites?: PendingWorkspaceInvite[];
    entitlements?: WorkspaceMembersEntitlements;
    pendingOwnerTransfer?: OwnerTransferPending | null;
    error?: Error | null;
    currentMembershipRole?: WorkspaceRole | null;
    currentWorkspaceRole?: string | null;
}): WorkspaceMembersState => ({
    workspaceId,
    status,
    members,
    pendingInvites,
    entitlements,
    pendingOwnerTransfer,
    error,
    currentMembershipRole,
    currentWorkspaceRole,
});

export const beginWorkspaceMembersLoad = (
    workspaceId: string | undefined,
    previousState: WorkspaceMembersState,
): WorkspaceMembersState => {
    if (!workspaceId) {
        return createWorkspaceMembersState({
            workspaceId: null,
            status: 'idle',
        });
    }

    const canRefreshCurrentWorkspace = previousState.workspaceId === workspaceId
        && (previousState.status === 'ready' || previousState.status === 'refreshing');

    if (canRefreshCurrentWorkspace) {
        return {
            ...previousState,
            status: 'refreshing',
            error: null,
        };
    }

    return createWorkspaceMembersState({
        workspaceId,
        status: 'loading',
    });
};

export const resolveWorkspaceMembersView = (
    workspaceId: string | undefined,
    state: WorkspaceMembersState,
): WorkspaceMembersState => {
    if (!workspaceId) {
        return createWorkspaceMembersState({
            workspaceId: null,
            status: 'idle',
        });
    }

    if (state.workspaceId !== workspaceId) {
        return createWorkspaceMembersState({
            workspaceId,
            status: 'loading',
        });
    }

    return state;
};

/**
 * 从 workspace_members 表拉取和管理当前 workspace 的成员列表
 */
export const useWorkspaceMembers = (
    workspaceId?: string,
    currentUserId?: string,
    userEmail?: string,
    options: UseWorkspaceMembersOptions = {},
): UseWorkspaceMembersReturn => {
    const isMemberManagementEnabled = options.enabled ?? true;
    const [invitations, setInvitations] = useState<WorkspaceInvitation[]>([]);
    const [isLoadingInvitations, setIsLoadingInvitations] = useState(true);
    const [workspaceMembersState, setWorkspaceMembersState] = useState<WorkspaceMembersState>(
        () => createWorkspaceMembersState({
            workspaceId: workspaceId || null,
            status: workspaceId && isMemberManagementEnabled ? 'loading' : 'idle',
        })
    );
    const membersRequestIdRef = useRef(0);
    const ownerTransferRequestIdRef = useRef(0);
    const invitationsRequestIdRef = useRef(0);
    const { set } = useUserStorage();
    

    const { showToast } = useToast();

    const workspaceMembersView = resolveWorkspaceMembersView(workspaceId, workspaceMembersState);

    const fetchPendingOwnerTransfer = useCallback(async () => {
        const requestId = ownerTransferRequestIdRef.current + 1;
        ownerTransferRequestIdRef.current = requestId;

        if (!workspaceId || !isMemberManagementEnabled) {
            setWorkspaceMembersState(prev => ({
                ...prev,
                pendingOwnerTransfer: null,
            }));
            return;
        }

        try {
            const pending = await featureService.getPendingOwnerTransfer(workspaceId);
            if (ownerTransferRequestIdRef.current !== requestId) {
                return;
            }

            setWorkspaceMembersState(prev => (
                prev.workspaceId === workspaceId
                    ? {
                        ...prev,
                        pendingOwnerTransfer: normalizePendingOwnerTransfer(pending, workspaceId),
                    }
                    : prev
            ));
        } catch (err: any) {
            if (ownerTransferRequestIdRef.current !== requestId) {
                return;
            }

            setWorkspaceMembersState(prev => (
                prev.workspaceId === workspaceId
                    ? {
                        ...prev,
                        pendingOwnerTransfer: null,
                    }
                    : prev
            ));
        }
    }, [isMemberManagementEnabled, workspaceId]);

    useEffect(() => {
        const refreshDelay = getPendingOwnerTransferRefreshDelay(workspaceMembersView.pendingOwnerTransfer?.expiresAt);
        if (refreshDelay === null) return;

        const timeoutId = window.setTimeout(() => {
            setWorkspaceMembersState(prev => ({
                ...prev,
                pendingOwnerTransfer: null,
            }));
            void fetchPendingOwnerTransfer();
        }, refreshDelay);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [workspaceMembersView.pendingOwnerTransfer?.expiresAt, fetchPendingOwnerTransfer]);

    // 监听 workspace_members 表的 UPDATE 事件
    useSupaSocket(
        {
            table: 'workspace_members',
            schema: 'public',
            event: 'UPDATE',
            enabled: isMemberManagementEnabled && !!workspaceId && !!currentUserId,
            filter: `workspace_id=eq.${workspaceId}`
        },
        {
            onUpdate: (payload) => {
                const updatedMember = payload.new as any;

                // 检查是否是其他成员的 status 被修改
                if (updatedMember.status === 'REMOVED') {
                    // 从 members 列表中移除该成员
                    setWorkspaceMembersState(prev => (
                        prev.workspaceId === workspaceId
                            ? {
                                ...prev,
                                members: prev.members.filter(m => m.id !== updatedMember.id),
                            }
                            : prev
                    ));

                    // 如果是当前用户被移除，显示提示并刷新页面
                    if (updatedMember.user_id === currentUserId) {
                        const currentWorkspaceStorageKey = getProductScopedStorageKey(
                            'currentWorkspaceId',
                            getCurrentProductCode(),
                        );
                        set(currentWorkspaceStorageKey, "");
                        showToast('You have been removed from this workspace', 'error');
                        setTimeout(() => {
                            window.location.reload();
                        }, 1500);
                    }
                }
            }
        }
    );

    /**
     * 从后端 API 拉取当前用户的待处理邀请列表
     */
    const fetchPendingInvitations = useCallback(async (email?: string) => {
        const requestId = invitationsRequestIdRef.current + 1;
        invitationsRequestIdRef.current = requestId;

        if (!email || !isMemberManagementEnabled) {
            setInvitations([]);
            setIsLoadingInvitations(false);
            return;
        }

        setIsLoadingInvitations(true);
        try {
            // Api.request 会自动提取 response.data.data，所以 response 直接就是邀请数组
            const invitationsData = await featureService.getPendingInvitations(email);
            if (invitationsRequestIdRef.current !== requestId) {
                return;
            }

            // response 已经是数组了
            setInvitations(Array.isArray(invitationsData) ? invitationsData : []);
        } catch (err: any) {
            if (invitationsRequestIdRef.current !== requestId) {
                return;
            }
            setInvitations([]);
        } finally {
            if (invitationsRequestIdRef.current === requestId) {
                setIsLoadingInvitations(false);
            }
        }
    }, [isMemberManagementEnabled]);

    const refreshInvitations = useCallback(
        () => fetchPendingInvitations(userEmail),
        [fetchPendingInvitations, userEmail],
    );

    /**
     * 从后端 API 拉取当前 workspace 的成员列表
     */
    const fetchMembers = useCallback(async () => {
        const requestId = membersRequestIdRef.current + 1;
        membersRequestIdRef.current = requestId;

        if (!workspaceId || !isMemberManagementEnabled) {
            setWorkspaceMembersState(createWorkspaceMembersState({
                workspaceId: workspaceId || null,
                status: 'idle',
            }));
            return;
        }

        setWorkspaceMembersState(prev => beginWorkspaceMembersLoad(workspaceId, prev));

        try {
            // 调用后端 API 获取成员列表
            const response = await featureService.getWorkspaceMembers(workspaceId);
            if (membersRequestIdRef.current !== requestId) {
                return;
            }

            // 新的 API 返回结构: { members, pendingInvites }
            const membersData = response.members || [];
            const pendingInvitesData = response.pendingInvites || [];

            // 转换成员数据为显示格式
            const formattedMembers: WorkspaceMemberDisplay[] = membersData.map((member: any) => {
                const name = member.name || member.email?.split('@')[0] || 'Unknown';
                const avatar = member.logoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=1A162F&color=fff`;

                return {
                    ...member,
                    displayName: name,
                    avatar: avatar,
                    isCurrentUser: currentUserId ? member.userId === currentUserId : false,
                };
            });

            // Membership 只表达 ownership；业务角色显示值来自 workspace role assignment。
            const currentMember = formattedMembers.find(m => m.isCurrentUser);
            setWorkspaceMembersState(prev => createWorkspaceMembersState({
                workspaceId,
                status: 'ready',
                members: formattedMembers,
                pendingInvites: pendingInvitesData,
                entitlements: response.entitlements || {},
                pendingOwnerTransfer: prev.workspaceId === workspaceId ? prev.pendingOwnerTransfer : null,
                currentMembershipRole: normalizeMembershipRoleValue(
                    currentMember?.membershipRole || currentMember?.role
                ),
                currentWorkspaceRole: currentMember?.role || currentMember?.workspaceRole || null,
            }));
        } catch (err: any) {
            if (membersRequestIdRef.current !== requestId) {
                return;
            }

            setWorkspaceMembersState(createWorkspaceMembersState({
                workspaceId,
                status: 'error',
                error: err instanceof Error ? err : new Error('Failed to load workspace members'),
            }));
        }
    }, [currentUserId, isMemberManagementEnabled, workspaceId]);

    // workspace roles now live in workspace_user_roles; refresh member rows
    // when another tab/user changes a member's assigned role.
    useSupaSocket(
        {
            table: 'workspace_user_roles',
            schema: 'public',
            event: '*',
            enabled: isMemberManagementEnabled && !!workspaceId && !!currentUserId,
            filter: `workspace_id=eq.${workspaceId}`
        },
        {
            onChange: () => {
                void fetchMembers();
            }
        }
    );

    // 初始化和 workspace 变化时拉取数据
    useEffect(() => {
        fetchMembers();
    }, [fetchMembers]);

    useEffect(() => {
        fetchPendingOwnerTransfer();
    }, [fetchPendingOwnerTransfer]);

    // 单独处理邀请列表，只在 userEmail 变化时调用
    useEffect(() => {
        refreshInvitations();
    }, [refreshInvitations]);

    /**
     * 邀请新成员到 workspace
     */
    const inviteMember = useCallback(
        async (
            email: string,
            role: WorkspaceRole,
            dataRoleId?: number | string | null,
            projectAccessGrants?: InviteProjectAccessGrant[],
        ) => {
            if (!workspaceId) {
                throw new Error('No workspace selected');
            }

            setWorkspaceMembersState(prev => ({
                ...prev,
                error: null,
            }));

            try {
                // 调用后端 API 发送邀请
                const response = await featureService.inviteWorkspaceMember(workspaceId, {
                    email,
                    role,
                    dataRoleId,
                    projectAccessGrants,
                });

                // 刷新成员列表
                await fetchMembers();
            } catch (err: any) {
                setWorkspaceMembersState(prev => ({
                    ...prev,
                    error: err instanceof Error ? err : new Error('Failed to invite workspace member'),
                }));
                throw err;
            }
        },
        [workspaceId, fetchMembers]
    );

    /**
     * 从 workspace 移除成员
     */
    const removeMember = useCallback(async (memberId: string) => {
        if (!workspaceId) {
            throw new Error('No workspace selected');
        }

        setWorkspaceMembersState(prev => ({
            ...prev,
            error: null,
        }));

        try {
            // 调用后端 API 移除成员
            const response = await featureService.removeWorkspaceMember(workspaceId, memberId);

            // 刷新成员列表
            await fetchMembers();
        } catch (err: any) {
            setWorkspaceMembersState(prev => ({
                ...prev,
                error: err instanceof Error ? err : new Error('Failed to remove workspace member'),
            }));
            throw err;
        }
    }, [workspaceId, fetchMembers]);

    /**
     * 更新 workspace 成员的角色
     */
    const updateMemberRole = useCallback(async (memberId: string, role: WorkspaceRole) => {
        if (!workspaceId) {
            throw new Error('No workspace selected');
        }

        setWorkspaceMembersState(prev => ({
            ...prev,
            error: null,
        }));

        try {
            // 调用后端 API 更新成员角色
            const response = await featureService.updateWorkspaceMemberRole(
                workspaceId,
                memberId,
                { role }
            );

            // 刷新成员列表
            await fetchMembers();
        } catch (err: any) {
            setWorkspaceMembersState(prev => ({
                ...prev,
                error: err instanceof Error ? err : new Error('Failed to update workspace member role'),
            }));
            throw err;
        }
    }, [workspaceId, fetchMembers]);

    const transferOwnership = useCallback(async (targetUserId: string, confirmationText: string) => {
        if (!workspaceId) {
            throw new Error('No workspace selected');
        }

        setWorkspaceMembersState(prev => ({
            ...prev,
            error: null,
        }));

        try {
            await featureService.transferWorkspaceOwnership(workspaceId, {
                targetUserId,
                confirmationText,
            });

            await fetchPendingOwnerTransfer();
        } catch (err: any) {
            setWorkspaceMembersState(prev => ({
                ...prev,
                error: err instanceof Error ? err : new Error('Failed to transfer workspace ownership'),
            }));
            throw err;
        }
    }, [workspaceId, fetchPendingOwnerTransfer]);

    const cancelOwnerTransfer = useCallback(async (requestId: string) => {
        if (!workspaceId) {
            throw new Error('No workspace selected');
        }

        setWorkspaceMembersState(prev => ({
            ...prev,
            error: null,
        }));

        try {
            await featureService.cancelOwnerTransfer(workspaceId, requestId);
            await fetchPendingOwnerTransfer();
        } catch (err: any) {
            setWorkspaceMembersState(prev => ({
                ...prev,
                error: err instanceof Error ? err : new Error('Failed to cancel workspace ownership transfer'),
            }));
            throw err;
        }
    }, [workspaceId, fetchPendingOwnerTransfer]);

    const cancelInvite = useCallback(async (token: string) => {
        if (!workspaceId) {
            throw new Error('No workspace selected');
        }

        setWorkspaceMembersState(prev => ({
            ...prev,
            error: null,
        }));

        try {
            await featureService.cancelWorkspaceInvite(workspaceId, token);
            await fetchMembers();
        } catch (err: any) {
            setWorkspaceMembersState(prev => ({
                ...prev,
                error: err instanceof Error ? err : new Error('Failed to cancel workspace invitation'),
            }));
            throw err;
        }
    }, [workspaceId, fetchMembers]);

    return {
        members: workspaceMembersView.members,
        invitations,
        pendingInvites: workspaceMembersView.pendingInvites,
        entitlements: workspaceMembersView.entitlements,
        pendingOwnerTransfer: workspaceMembersView.pendingOwnerTransfer,
        status: workspaceMembersView.status,
        isLoading: workspaceMembersView.status === 'loading',
        isLoadingInvitations,
        error: workspaceMembersView.error,
        refresh: fetchMembers,
        refreshInvitations,
        refreshPendingOwnerTransfer: fetchPendingOwnerTransfer,
        inviteMember,
        removeMember,
        updateMemberRole,
        transferOwnership,
        cancelOwnerTransfer,
        cancelInvite,
        currentMembershipRole: workspaceMembersView.currentMembershipRole,
        currentWorkspaceRole: workspaceMembersView.currentWorkspaceRole,
    };
};
